import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { SettingField } from "@/components/settings/setting-field";
import { EnumSelect } from "@/components/ui/enum-select";
import { FormControl, FormField, FormItem } from "@/components/ui/form";
import { Switch } from "@/components/ui/switch";
import type { SaveField } from "../../../hooks/use-auto-save-field";
import { SalonConfig } from "../hooks/use-salon-config";
import { getTranslatedOutcomeTrackingModeOptions } from "../utils/outcome-tracking-enums";

export function OutcomeTrackingConfig({
  saveField,
  saving,
}: {
  saveField: SaveField<SalonConfig>;
  saving: boolean;
}) {
  const { control } = useFormContext<SalonConfig>();
  const { t } = useTranslation("settings");
  const outcomeTrackingEnabled = useWatch({
    control,
    name: "outcomeTrackingEnabled",
  });
  const _outcomeTrackingMode = useWatch({
    control,
    name: "outcomeTrackingMode",
  });

  const translatedOptions = getTranslatedOutcomeTrackingModeOptions(t);

  return (
    <div className="space-y-6">
      {/* Enable/Disable Outcome Tracking */}
      <FormField
        control={control}
        name="outcomeTrackingEnabled"
        render={({ field }) => (
          <FormItem id="field-salon-outcome-tracking">
            <SettingField
              label={t("salonConfig.enableOutcomeTracking")}
              helpText={t("salonConfig.enableOutcomeTrackingDescription")}
              helpDetails={
                <div className="space-y-2">
                  <div className="text-sm font-medium text-foreground">
                    {t("salonConfig.fields.outcomeTracking.helpDetails.title")}
                  </div>
                  <ul className="text-sm space-y-1 text-muted-foreground">
                    {(
                      t("salonConfig.fields.outcomeTracking.helpDetails.benefits", {
                        returnObjects: true,
                      }) as string[]
                    ).map((benefit: string, index: number) => (
                      <li key={index}>• {benefit}</li>
                    ))}
                  </ul>
                </div>
              }
            >
              <FormControl>
                <Switch
                  checked={field.value}
                  disabled={saving}
                  onCheckedChange={(value) => {
                    void saveField("outcomeTrackingEnabled", value);
                  }}
                />
              </FormControl>
            </SettingField>
          </FormItem>
        )}
      />

      {/* Outcome Tracking Mode */}
      {outcomeTrackingEnabled && (
        <FormField
          control={control}
          name="outcomeTrackingMode"
          render={({ field }) => (
            <FormItem id="field-salon-tracking-mode">
              <SettingField
                label={t("salonConfig.fields.trackingMode.label")}
                helpText={t("salonConfig.fields.trackingMode.helpText")}
                helpDetails={
                  <div className="space-y-3">
                    <div className="text-sm font-medium text-foreground">
                      {t("salonConfig.fields.trackingMode.helpDetails.title")}
                    </div>
                    <ul className="text-sm space-y-1 text-muted-foreground">
                      {(
                        t("salonConfig.fields.trackingMode.helpDetails.benefits", {
                          returnObjects: true,
                        }) as string[]
                      ).map((benefit: string, index: number) => (
                        <li key={index}>• {benefit}</li>
                      ))}
                    </ul>
                  </div>
                }
              >
                <FormControl>
                  <EnumSelect
                    value={field.value}
                    onValueChange={field.onChange}
                    options={translatedOptions}
                    placeholder={t("salonConfig.selectTrackingMode")}
                  />
                </FormControl>
              </SettingField>
            </FormItem>
          )}
        />
      )}
    </div>
  );
}
